import React from 'react';
import { useTheme } from '@material-ui/core';
import { TaxiCompanyList, isFukushimaTaxi } from '../../../../../services/taxi/GetDirectionAndFare/models/getFareSamplesParams';
import { AMCCaution } from './AMCCaution';
import { useCautionStyles } from './Cautions';
import { EasternFareRevisionCaution } from './EasternFareRevisionCaution';

interface Props {
  isMeterFare: boolean,
  /** 仮予約画面で表示するタクシー会社名 */
  companyDisplayName: string,
  /** AMCをサポートする事業者の場合 */
  isSupportAMC?: boolean,
  /** 本予約画面か */
  isReservationPage?: boolean,
  /** タクシー会社 */
  company: TaxiCompanyList,
  /** 【AM-1152】マイル増量キャンペーン期間中か */
  isCampaignPeriod?: boolean,
  /** 【AM-1152】通常マイルあり（元々AMC ON）の会社か */
  isOriginalSupported?: boolean,
}

/** ハイヤー・タクシー予約画面の注意事項のコンポーネント。 */
export const CautionsJP: React.FC<Props> = (props: Props) => {
  const theme = useTheme();
  const classes = useCautionStyles(theme);
  /** 福島タクシー群か */
  const isFukushima = isFukushimaTaxi(props.company);
  /** 稲荷タクシーか？ */
  const isInariTaxi = [TaxiCompanyList.inari, TaxiCompanyList.inarihw].includes(props.company);
  /** 立ち寄りが不可かの文言: 福島タクシー系列かキングタクシー以外の場合は可 */
  const tachiyoriMessage = (() => {
    /** 福島タクシー群・キングタクシーの場合のみ乗合タクシーのため立ち寄り不可と明言する */
    if(isFukushima) {
      return '指定いただいた乗車地・降車地以外の立ち寄りは出来ません。';
    }
    switch(props.company) {
      case TaxiCompanyList.kingTaxi:
      case TaxiCompanyList.sakataNo1Taxi:
      case TaxiCompanyList.shokoHire:
        return '指定いただいた乗車地・降車地以外の立ち寄りは出来ません。';
      default:
        /** それ以外のタクシー会社なら言えば出来るよみたいなニュアンスにする */
        return `指定いただいた乗車地・降車地以外の立ち寄りは含まれておりません。`
          + `立ち寄りを希望される場合には、お客様ご自身で${props.companyDisplayName}へお問い合わせください。`
    }
  })();

  return (
    <div className={classes.root} data-cy="cautions-area">
      <span className={classes.bold}>＜注意事項＞</span>
      <ul className={classes.unorderedList}>
        <li>
          ご予約は{props.companyDisplayName}とお客様の直接契約となります。
          {isInariTaxi &&
            <>運行は、稲荷タクシー又は稲荷タクシーグループとなります。</>
          }
        </li>
        {/* 稲荷タクシー（高速料金）の場合には別途文言を表示する */}
        {props.company === TaxiCompanyList.inarihw &&
          <li className={classes.redFont}>
            高速利用の場合、走行距離の関係により一般道利用とは料金が異なります。さらに、高速代が別途必要となります。
          </li>
        }
        <li className={classes.redFont}>
          お客様の入力した情報は{props.companyDisplayName}に通知されます。また、問い合わせ等への対応のため、本画面にて入力いただいたEメールアドレスはANAにも通知されますが、通常1年以内に削除いたします。
        </li>
        {/** AMCサポート事業者の場合には文言を追加する。本予約画面のみ表示する */}
        {props.isSupportAMC && props.isReservationPage &&
          <li>
            本画面にて入力いただいたANAマイレージクラブお客様番号（10桁）は、マイルの積算のためANAへ連携いたします。
          </li>
        }
        <li className={classes.redFont}>
          予約リクエストが完了しても予約が確定したわけではありません。{props.companyDisplayName}の「確定メール」をもって予約の確定としています。あらかじめご了承ください。
        </li>
        {/** 福島タクシー群の場合のみ乗合タクシー表記を追加する */}
        {isFukushima &&
          <li className={classes.redFont}>
            乗合タクシーのため、他のお客様と混乗となる場合があります。空港にお送りする場合、お迎え先の乗車時間は変更となる場合があります。
            （その場合は、前日の18時までにご連絡いたします）
            また、空港発の場合、降車順により所要時間が変動する場合があります。
          </li>
        }
        {/** 伊予鉄メーター料金・北鹿観光ハイヤーの場合には別途文言を表示する */}
        {(props.company === TaxiCompanyList.iyotetsuTaxi2 || props.company === TaxiCompanyList.hokuroku) &&
          <li className={classes.redFont}>
            本画面に表示されている金額はあくまで目安となります。タクシー会社からの「ご予約確定メール」にて確定料金をご確認いただけます。
          </li>
        }
        {/** 大分別府合同タクシーの場合には別途文言を表示する */}
        {props.company === TaxiCompanyList.gotaku &&
          <>
            <li className={classes.redFont}>
              イベントやスポーツ会場発（お迎え）→大分空港は、ご予約いただけません。
            </li>
            <li className={classes.redFont}>
              料金には、目的地または出発地により「高速道路代金」が含まれております。詳しくは、「予約確定メール」にてご確認下さい。
            </li>
          </>
        }
        {/** キングタクシーの場合スペシャルメッセージを表示する */}
        {(props.company === TaxiCompanyList.kingTaxi) &&
          <li>
            乗合運行のため複数の航空便ご利用のお客様と混乗となっております。
            飛行機の遅延により待機する場合は、 先着のお客様乗車時に最大10分間、他の便到着を待っての待機延長を了承された場合のみとなります。
            また乗車予定者が、1名様のみの場合は、出発予定時刻の30分まで待機延長しますが、それ以降のご到着の場合はキャンセルの取扱いとなります。
          </li>
        }
        <li>
          大変お手数をおかけいたしますが、ご予約の際には、毎回メールアドレスの認証が必要となります。
        </li>
        <li>
          ご連絡のないキャンセルや、当日のキャンセルは100％のキャンセル料が発生致します。
        </li>
        <li>
          飛行機の遅延による空港待機料金、欠航によりご乗車できなかった場合のキャンセル料金はかかりません。
          ただし、ANAからの情報の引継ぎは行われません。
          <br />
          大変お手数ですが、お客様ご自身で{props.companyDisplayName}へお問い合わせください。
        </li>
        <li>
          万が一交通渋滞等でフライト出発時間に間に合わない場合も、航空券予約の保証はいたしかねます。
        </li>
        <li>
          お支払いは現地車内にてお願いいたします。
        </li>
        {/* 立ち寄りに関するメッセージを表示する */}
        <li>
          {tachiyoriMessage}
        </li>
        {/** イースタンエアポートモーターズ(limoTaxi)の場合にはハイヤー料金改定のお知らせを表示する */}
        {props.company === TaxiCompanyList.limoTaxi &&
          <EasternFareRevisionCaution />
        }
        {/* メーター料金の場合には注意書きを増やします */}
        {props.isMeterFare &&
          <li>
            タクシーのメーター料金については想定走行距離を基に算出した目安金額であり、道路事情等により実際と異なる場合がございます。
            また高速代金や回送料金、待機や信号・渋滞等による停車などの時間は考慮しておりません｡各タクシー会社や地域により料金は異なることがありますので、あくまで参考金額としてご確認ください。実際の料金は乗車時のメーターに基づいての精算となります。
          </li>
        }
        {/** 伊予鉄タクシーの場合には高速道路代金の注意事項を追加 */}
        {props.company === TaxiCompanyList.iyotetsutaxi &&
          <li>
            料金には、目的地または出発地により「高速道路代金」が含まれております。詳しくは、「予約確定メール」にてご確認下さい。
          </li>
        }
        {/** キングタクシーの場合には乗り合いタクシーの注釈を追加 */}
        { props.company === TaxiCompanyList.kingTaxi &&
          <li>
            乗合タクシーは、（空港⇔角館地区・田沢湖地区・田沢湖高原地区・乳頭温泉地区）、（空港⇔男鹿地区・戸賀地区・加茂地区）の運行となるため、場所・宿泊施設によっては、いくつかを経由いたします。
            また、最終的なお迎え時間はタクシー会社からご連絡いたします。
          </li>
        }

      </ul>
      {/** AMCをサポートしている場合にはAMCの注意事項を追加する */}
      {props.isSupportAMC && props.isReservationPage &&
        <AMCCaution
          companyDisplayName={props.companyDisplayName}
          company={props.company}
          isCampaignPeriod={props.isCampaignPeriod}
          isOriginalSupported={props.isOriginalSupported}
        />
      }
    </div>
  );
}

export default CautionsJP;
